import { useState } from "react";
import { toast } from "sonner";
import { Trash2, X, Loader2, AlertTriangle } from "lucide-react";

/**
 * Einheitlicher Löschen-Dialog mit Cascade-Wahl für alle CRM-Entities.
 * - onConfirm(withFiles: boolean) wird beim Klick auf Löschen aufgerufen.
 * - Nach Erfolg -> navigate/redirect erledigt der Aufrufer.
 */
export function DeleteEntityButton({ label = "Löschen", entityName, onConfirm, redirectTo = "/drive/crm" }) {
  const [open, setOpen] = useState(false);
  const [withFiles, setWithFiles] = useState(false);
  const [busy, setBusy] = useState(false);

  const runDelete = async () => {
    setBusy(true);
    try {
      await onConfirm(withFiles);
      toast.success(`${entityName || "Eintrag"} gelöscht`);
      setOpen(false);
      if (redirectTo) window.location.assign(redirectTo);
    } catch (e) {
      toast.error(e.message || "Löschen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="btn-delete-entity"
        className="inline-flex items-center gap-2 border border-[#ff0033]/40 text-[#ff0033] hover:bg-[#ff0033] hover:text-white px-4 py-3 text-[10px] font-bold tracking-[0.3em] uppercase transition-colors"
      >
        <Trash2 size={13} /> {label}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#ff0033]/40 max-w-lg w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-3">
                <AlertTriangle size={18} className="text-[#ff0033]" />
                <div className="font-display font-black uppercase text-lg tracking-tight">{entityName ? `${entityName} löschen?` : "Wirklich löschen?"}</div>
              </div>
              <button onClick={() => !busy && setOpen(false)} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-sm text-zinc-300 leading-relaxed">
                Dieser Vorgang kann nicht rückgängig gemacht werden. Zugehörige Verträge, Dateien und Rechnungen bleiben standardmäßig erhalten und werden lediglich vom Kontakt entkoppelt.
              </p>
              <label className="flex items-start gap-3 cursor-pointer bg-[#050505] border border-white/10 p-4 hover:border-[#ff0033]/40 transition-colors">
                <input
                  type="checkbox"
                  checked={withFiles}
                  onChange={(e) => setWithFiles(e.target.checked)}
                  data-testid="delete-with-files"
                  className="mt-0.5 w-4 h-4 accent-[#ff0033]"
                />
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-1">Auch alle Verträge & Dateien löschen</div>
                  <div className="text-xs text-zinc-500">Löscht zusätzlich alle diesem Kontakt zugeordneten Verträge, Rechnungen und Dateien im Storage.</div>
                </div>
              </label>
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/[0.06]">
                <button
                  onClick={() => setOpen(false)}
                  disabled={busy}
                  className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 hover:text-white"
                >
                  Abbrechen
                </button>
                <button
                  onClick={runDelete}
                  disabled={busy}
                  data-testid="btn-confirm-delete"
                  className="inline-flex items-center gap-2 bg-[#ff0033] text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:shadow-[0_0_20px_rgba(255,0,51,0.5)] transition-all disabled:opacity-50"
                >
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  {busy ? "Lösche…" : "Endgültig löschen"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
